import React from 'react';
import { ExternalLink, Sparkles, X } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { CATEGORY_BADGE_CLASS } from '@/utils/tabCategories';
import { formatTabTimestamp, type TabPredictionRow, type TabRow } from './tabIntelUtils';

export type { TabPredictionRow, TabRow };

export function TabDetailStrip({
  tab,
  onClose,
}: {
  tab: TabRow;
  onClose: () => void;
}) {
  const ts = formatTabTimestamp(tab.lastAccessedAt);
  return (
    <Card className="p-4 border border-primary-500/25 bg-primary-500/5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-khiimori-text-primary truncate">{tab.title}</p>
          <p className="text-xs text-khiimori-text-muted truncate mt-0.5">{tab.url}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 rounded-lg hover:bg-khiimori-bg-elevated text-khiimori-text-muted"
          aria-label="Close tab details"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="mt-3 flex flex-wrap gap-2 text-xs">
        <span className={`px-2 py-1 rounded-full ${CATEGORY_BADGE_CLASS[tab.category]}`}>{tab.category}</span>
        <span className="px-2 py-1 rounded-full bg-khiimori-bg-elevated text-khiimori-text-secondary">
          Activity {tab.activity}%
        </span>
        <span className="px-2 py-1 rounded-full bg-khiimori-bg-elevated text-khiimori-text-secondary">
          Security {tab.securityScore ?? 'not scanned'}
        </span>
        <span className="px-2 py-1 rounded-full bg-khiimori-bg-elevated text-khiimori-text-secondary">
          {tab.relationships} linked tab{tab.relationships === 1 ? '' : 's'}
        </span>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
        <span className="text-khiimori-text-muted">{ts?.full ?? '—'}</span>
        <span className="text-purple-400 flex items-center gap-1">
          <Sparkles className="w-3.5 h-3.5" />
          {tab.prediction}
        </span>
        {tab.url && (
          <a
            href={tab.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-primary-400 hover:text-primary-300"
          >
            Open <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
      </div>
    </Card>
  );
}
